import React, { useState, useEffect } from 'react';
import { reportCardService } from '../../../services/index.js';
import { Plus, Trash2, Save, GripVertical, AlertCircle } from 'lucide-react';

const CATEGORY_OPTIONS = [
  { value: 'FORMATIVE', label: 'Formative Assessment' },
  { value: 'SUMMATIVE', label: 'Summative Assessment' },
  { value: 'UNIT_TEST', label: 'Unit Test' },
  { value: 'PERIODIC_TEST', label: 'Periodic Test' },
  { value: 'TERM_EXAM', label: 'Term / Annual Exam' },
  { value: 'OTHER', label: 'Other' }
];

const DEFAULT_SUGGESTIONS = [
  { sortOrder: 1, examCode: 'FA-1', examName: 'Formative Assessment 1', examCategory: 'FORMATIVE', defaultMaxMarks: 20, termLabel: 'Term 1' },
  { sortOrder: 2, examCode: 'FA-2', examName: 'Formative Assessment 2', examCategory: 'FORMATIVE', defaultMaxMarks: 20, termLabel: 'Term 1' },
  { sortOrder: 3, examCode: 'SA-1', examName: 'Summative Assessment 1', examCategory: 'SUMMATIVE', defaultMaxMarks: 80, termLabel: 'Term 1' },
  { sortOrder: 4, examCode: 'FA-3', examName: 'Formative Assessment 3', examCategory: 'FORMATIVE', defaultMaxMarks: 20, termLabel: 'Term 2' },
  { sortOrder: 5, examCode: 'FA-4', examName: 'Formative Assessment 4', examCategory: 'FORMATIVE', defaultMaxMarks: 20, termLabel: 'Term 2' },
  { sortOrder: 6, examCode: 'SA-2', examName: 'Summative Assessment 2', examCategory: 'SUMMATIVE', defaultMaxMarks: 80, termLabel: 'Term 2' }
];

const ExamTypeSettings = ({
  activeAcademicYear,
  onSaved,
  showSuccessNotification,
  showErrorNotification
}) => {
  const [academicYear, setAcademicYear] = useState(activeAcademicYear || '2026-27');
  const [examTypes, setExamTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (activeAcademicYear) setAcademicYear(activeAcademicYear);
  }, [activeAcademicYear]);

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicYear]);

  const loadConfig = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await reportCardService.getExamTypeConfig(academicYear);
      if (res && res.success && Array.isArray(res.examTypes)) {
        if (res.examTypes.length > 0) {
          setExamTypes(res.examTypes.map(e => ({ ...e })));
        } else {
          // No config yet for this year — leave empty, show suggestion banner
          setExamTypes([]);
        }
      } else {
        setExamTypes([]);
      }
      setDirty(false);
    } catch (err) {
      setLoadError('Failed to load exam type configuration.');
      setExamTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSuggestions = () => {
    if (examTypes.length > 0 && !window.confirm('This will replace your current list with the default suggestions. Continue?')) return;
    setExamTypes(DEFAULT_SUGGESTIONS.map(s => ({ ...s })));
    setDirty(true);
  };

  const handleAdd = () => {
    const nextOrder = examTypes.length > 0 ? Math.max(...examTypes.map(e => e.sortOrder || 0)) + 1 : 1;
    setExamTypes([...examTypes, {
      sortOrder: nextOrder,
      examCode: '',
      examName: '',
      examCategory: 'FORMATIVE',
      defaultMaxMarks: 20,
      termLabel: 'Term 1'
    }]);
    setDirty(true);
  };

  const handleChange = (idx, field, value) => {
    const next = examTypes.map((e, i) => i === idx ? { ...e, [field]: value } : e);
    setExamTypes(next);
    setDirty(true);
  };

  const handleDelete = (idx) => {
    if (!window.confirm(`Remove "${examTypes[idx].examCode || 'this exam type'}" from the list?`)) return;
    const next = examTypes.filter((_, i) => i !== idx).map((e, i) => ({ ...e, sortOrder: i + 1 }));
    setExamTypes(next);
    setDirty(true);
  };

  const handleMoveUp = (idx) => {
    if (idx === 0) return;
    const next = [...examTypes];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    setExamTypes(next.map((e, i) => ({ ...e, sortOrder: i + 1 })));
    setDirty(true);
  };

  const handleMoveDown = (idx) => {
    if (idx === examTypes.length - 1) return;
    const next = [...examTypes];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    setExamTypes(next.map((e, i) => ({ ...e, sortOrder: i + 1 })));
    setDirty(true);
  };

  const handleSave = async () => {
    // Validate
    for (let i = 0; i < examTypes.length; i++) {
      const e = examTypes[i];
      if (!e.examCode || !e.examCode.trim()) {
        alert(`Row ${i + 1}: Exam Code is required.`);
        return;
      }
      if (!e.examName || !e.examName.trim()) {
        alert(`Row ${i + 1}: Exam Name is required.`);
        return;
      }
      if (!e.defaultMaxMarks || parseInt(e.defaultMaxMarks, 10) <= 0) {
        alert(`Row ${i + 1}: Max Marks must be greater than 0.`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = examTypes.map((e, i) => ({
        sortOrder: i + 1,
        examCode: e.examCode.trim(),
        examName: e.examName.trim(),
        examCategory: e.examCategory || 'OTHER',
        defaultMaxMarks: parseInt(e.defaultMaxMarks, 10),
        termLabel: (e.termLabel || '').trim()
      }));

      const res = await reportCardService.saveExamTypeConfig(academicYear, payload);
      if (res && res.success) {
        showSuccessNotification('Exam Types Saved', `${payload.length} exam type(s) saved for ${academicYear}.`);
        setDirty(false);
        await loadConfig();
        if (typeof onSaved === 'function') {
          onSaved();
        }
      } else {
        showErrorNotification('Save Failed', res?.error || 'Failed to save exam types.');
      }
    } catch (err) {
      showErrorNotification('Error', err.message);
    } finally {
      setSaving(false);
    }
  };

  const categoryLabel = (cat) => {
    const found = CATEGORY_OPTIONS.find(c => c.value === cat);
    return found ? found.label : cat;
  };

  const categoryColor = (cat) => {
    const map = { FORMATIVE: '#2563eb', SUMMATIVE: '#16a34a', UNIT_TEST: '#d97706', PERIODIC_TEST: '#7c3aed', TERM_EXAM: '#dc2626', OTHER: '#64748b' };
    return map[cat] || '#64748b';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>Exam Types</h4>
          <p style={{ margin: '3px 0 0 0', fontSize: '12.5px', color: '#64748b' }}>
            Configure the examination catalog for each academic year. Each school can define its own pattern.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleLoadSuggestions}
            style={{ padding: '7px 14px', borderRadius: '7px', border: '1.5px solid #cbd5e1', background: '#f8fafc', fontSize: '12.5px', fontWeight: '700', color: '#475569', cursor: 'pointer' }}
          >
            Load Defaults
          </button>
          <button
            type="button"
            onClick={handleAdd}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '7px', border: 'none', background: '#1e3a8a', color: '#fff', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer' }}
          >
            <Plus size={14} /> Add Exam Type
          </button>
          {dirty && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 16px', borderRadius: '7px', border: 'none', background: '#16a34a', color: '#fff', fontSize: '12.5px', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer' }}
            >
              <Save size={14} /> {saving ? 'Saving…' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>

      {/* Info banner if no config yet */}
      {!loading && examTypes.length === 0 && !loadError && (
        <div style={{ background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: '10px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <AlertCircle size={18} color="#2563eb" />
          <div>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e3a8a' }}>No exam types configured for {academicYear}</div>
            <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px' }}>
              Click <strong>Load Defaults</strong> for common FA/SA patterns, or click <strong>Add Exam Type</strong> to create your own.
            </div>
          </div>
        </div>
      )}

      {loadError && (
        <div style={{ background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: '10px', padding: '12px 16px', color: '#dc2626', fontSize: '13px' }}>
          {loadError}
        </div>
      )}

      {loading && (
        <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
          Loading exam types for {academicYear}…
        </div>
      )}

      {/* Table */}
      {!loading && examTypes.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
          {/* Column Headers */}
          <div style={{ display: 'grid', gridTemplateColumns: '32px 50px 100px 1fr 150px 80px 90px 80px', gap: '0', background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0', padding: '8px 12px', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>
            <div></div>
            <div>#</div>
            <div>Code</div>
            <div>Name</div>
            <div>Category</div>
            <div>Max Marks</div>
            <div>Term</div>
            <div></div>
          </div>

          {examTypes.map((et, idx) => (
            <div key={idx} style={{
              display: 'grid',
              gridTemplateColumns: '32px 50px 100px 1fr 150px 80px 90px 80px',
              gap: '0',
              alignItems: 'center',
              padding: '8px 12px',
              borderBottom: idx < examTypes.length - 1 ? '1px solid #f1f5f9' : 'none',
              background: '#fff'
            }}>
              {/* Reorder buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                <button type="button" onClick={() => handleMoveUp(idx)} disabled={idx === 0}
                  style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer', color: idx === 0 ? '#cbd5e1' : '#94a3b8', padding: '1px', fontSize: '10px' }}>▲</button>
                <button type="button" onClick={() => handleMoveDown(idx)} disabled={idx === examTypes.length - 1}
                  style={{ background: 'none', border: 'none', cursor: idx === examTypes.length - 1 ? 'default' : 'pointer', color: idx === examTypes.length - 1 ? '#cbd5e1' : '#94a3b8', padding: '1px', fontSize: '10px' }}>▼</button>
              </div>

              {/* Order number */}
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textAlign: 'center' }}>
                {idx + 1}
              </div>

              {/* Exam Code */}
              <div style={{ paddingRight: '8px' }}>
                <input
                  type="text"
                  value={et.examCode || ''}
                  onChange={e => handleChange(idx, 'examCode', e.target.value)}
                  placeholder="FA-1"
                  style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1.5px solid #cbd5e1', fontSize: '12.5px', fontWeight: '700', boxSizing: 'border-box' }}
                />
              </div>

              {/* Exam Name */}
              <div style={{ paddingRight: '8px' }}>
                <input
                  type="text"
                  value={et.examName || ''}
                  onChange={e => handleChange(idx, 'examName', e.target.value)}
                  placeholder="Formative Assessment 1"
                  style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1.5px solid #cbd5e1', fontSize: '12.5px', boxSizing: 'border-box' }}
                />
              </div>

              {/* Category */}
              <div style={{ paddingRight: '8px' }}>
                <select
                  value={et.examCategory || 'FORMATIVE'}
                  onChange={e => handleChange(idx, 'examCategory', e.target.value)}
                  style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1.5px solid #cbd5e1', fontSize: '12px', background: '#fff', boxSizing: 'border-box', fontWeight: '600', color: categoryColor(et.examCategory) }}
                >
                  {CATEGORY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Max Marks */}
              <div style={{ paddingRight: '8px' }}>
                <input
                  type="number"
                  min={1}
                  max={500}
                  value={et.defaultMaxMarks || 20}
                  onChange={e => handleChange(idx, 'defaultMaxMarks', e.target.value)}
                  style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1.5px solid #cbd5e1', fontSize: '12.5px', fontWeight: '700', boxSizing: 'border-box' }}
                />
              </div>

              {/* Term Label */}
              <div style={{ paddingRight: '8px' }}>
                <input
                  type="text"
                  value={et.termLabel || ''}
                  onChange={e => handleChange(idx, 'termLabel', e.target.value)}
                  placeholder="Term 1"
                  style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1.5px solid #cbd5e1', fontSize: '12px', boxSizing: 'border-box' }}
                />
              </div>

              {/* Delete */}
              <div>
                <button
                  type="button"
                  onClick={() => handleDelete(idx)}
                  style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px 6px', borderRadius: '5px' }}
                  title="Remove this exam type"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Save bar at bottom when dirty */}
      {dirty && examTypes.length > 0 && (
        <div style={{ background: '#1e3a8a', borderRadius: '8px', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#93c5fd', fontSize: '12.5px', fontWeight: '600' }}>
            Unsaved changes — click Save to apply for {academicYear}.
          </span>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 18px', borderRadius: '6px', background: '#fff', color: '#1e3a8a', border: 'none', fontSize: '13px', fontWeight: '800', cursor: saving ? 'not-allowed' : 'pointer' }}
          >
            <Save size={14} /> {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      )}

    </div>
  );
};

export default ExamTypeSettings;
